import React, { BaseSyntheticEvent, useState } from 'react'
import {
  IonButton,
  IonContent,
  IonHeader,
  IonItem,
  IonList,
  IonToggle,
  IonToolbar,
  IonButtons,
  IonIcon,
  IonTitle,
  IonLabel,
  IonCard,
  IonSearchbar,
} from '@ionic/react'
import { IOption, IOptions } from '../../../common/common.types'
import { getPreferredLanguageOptionsSelector } from '../../options/selectors'
import { useDispatch, useSelector } from 'react-redux'
import { getPatientDemographicsSelector } from '../selectors'
import { savePatientDemographics, setPreferredLanguage } from '../store/patientDemographics.actions'
import { chevronBackOutline } from 'ionicons/icons'
import { useTranslation } from 'react-i18next'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { useHistory } from 'react-router'
import useDeviceMetrics from '../../../utilities/useDeviceMetrics'
import { PatientInformationConstants } from '../../patientInformation/store/patientInformation.constants'
import { setSelectedOption, enableScroll } from '../../patientInformation/store/patientInformation.actions'
import './PreferredLanguage.content.css'

const PreferredLanguageContent: React.FC = () => {
  const { t } = useTranslation()
  const { preferredLanguageOptions } = useSelector(getPreferredLanguageOptionsSelector)
  const { preferredLanguage } = useSelector(getPatientDemographicsSelector)
  const dispatch = useDispatch()
  const history = useHistory()
  const { isTablet } = useDeviceMetrics()
  const [searchValue, setSearchValue] = useState('')
  const [filteredOptions, setFilteredOptions] = useState(preferredLanguageOptions)

  const currentURL = history.location.pathname

  const handleToggle = (e: BaseSyntheticEvent, option: IOption) => {
    e.preventDefault()
    const updatedPreferredLanguage: IOptions = [...preferredLanguage]
    if (e.target.checked) {
      updatedPreferredLanguage.push(option)
    } else {
      const index = updatedPreferredLanguage.findIndex((opt) => opt.id === option.id)
      if (index !== -1) {
        updatedPreferredLanguage.splice(index, 1)
      }
    }
    dispatch(setPreferredLanguage(updatedPreferredLanguage))
    dispatch(savePatientDemographics())
  }

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (isTablet) {
      dispatch(setSelectedOption(PatientInformationConstants.PATIENT_DEMOGRAPHICS_OPTION))
      if (currentURL !== TabConstants.PATIENT_INFO_ROUTE) {
        history.push(TabConstants.PATIENT_INFO_ROUTE)
      }
    } else {
      history.push(TabConstants.PATIENT_DEMOGRAPHICS)
    }
    dispatch(enableScroll(true))
    setSearchValue('')
    setFilteredOptions(preferredLanguageOptions)
  }

  const handleSearch = (e: CustomEvent) => {
    const searchText = e.detail.value
    setSearchValue(searchText)

    const result = preferredLanguageOptions.filter((option) =>
      option.name?.toLowerCase().includes(searchText.toLowerCase())
    )
    setFilteredOptions(result)
  }

  return (
    <>
      <IonHeader className='ion-no-border' id='PreferredLanguageHeader'>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' fill='clear' onClick={onBackClick} color='primary'>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonTitle>{t('patientDemographics.details.PreferredLanguage')}</IonTitle>
        </IonToolbar>
        <IonToolbar>
          <IonSearchbar
            className='pb-0'
            data-testid='PreferredLanguageHeaderSearchbar'
            debounce={100}
            value={searchValue}
            onIonChange={handleSearch}
            autocomplete='off'
          />
        </IonToolbar>
      </IonHeader>

      <IonContent color='light' fullscreen={false} className={isTablet ? 'preferred-language-content-tablet' : ''}>
        <IonCard className={isTablet ? 'card-width' : 'card-margin'}>
          {searchValue && filteredOptions.length === 0 && (
            <IonItem data-testid='NoOptions'>
              <IonLabel>
                {t('common.NoSearchResults')}
                {`"${searchValue}"`}
              </IonLabel>
            </IonItem>
          )}
          <IonList id='multiSelectList'>
            {filteredOptions &&
              filteredOptions.map((option: IOption) => (
                <IonItem key={`${option.name}_${option.id}`} data-skip-auto-enhance>
                  <IonLabel>{option.name}</IonLabel>
                  <IonToggle
                    slot='start'
                    name={option.name}
                    value={`${option.id}`}
                    checked={preferredLanguage.some((opt) => opt.id === option.id)}
                    onClick={(e) => handleToggle(e, option)}
                  />
                </IonItem>
              ))}
          </IonList>
        </IonCard>
      </IonContent>
    </>
  )
}

export default PreferredLanguageContent
